import * as React from 'react';
import {
  Location,
  UNSAFE_NavigationContext as NavigationContext,
  useBeforeUnload,
} from 'react-router-dom';

type BlockedNavigation = {
  proceed: () => void;
  reset: () => void;
  location: Location;
};

type BlockedHandler = (navigation: BlockedNavigation) => void;

type BlockerArgs = {
  location: Location;
  retry: () => void;
};

const useHistoryBlocker = (blocker: (args: BlockerArgs) => void, when: boolean) => {
  const { navigator } = React.useContext(NavigationContext);

  React.useEffect(() => {
    if (!when) {
      return;
    }

    if (!navigator || typeof (navigator as any).block !== 'function') {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Navigator instance does not support blocking navigation.');
      }
      return;
    }

    const unblock = (navigator as any).block(
      (tx: { location: Location; retry: () => void }) => {
        const retry = () => {
          unblock();
          tx.retry();
        };
        blocker({ location: tx.location, retry });
      }
    );

    return unblock;
  }, [navigator, blocker, when]);
};

export function useUnsavedChangesPrompt(when: boolean, onBlocked: BlockedHandler) {
  const latestHandler = React.useRef(onBlocked);
  const latestWhen = React.useRef(when);

  React.useEffect(() => {
    latestHandler.current = onBlocked;
  }, [onBlocked]);

  React.useEffect(() => {
    latestWhen.current = when;
  }, [when]);

  useHistoryBlocker(
    React.useCallback(
      ({ location, retry }) => {
        if (!latestWhen.current) {
          return;
        }
        latestHandler.current({
          proceed: retry,
          reset: () => {},
          location,
        });
      },
      []
    ),
    when
  );

  useBeforeUnload(
    React.useCallback((event: BeforeUnloadEvent) => {
      if (!latestWhen.current) {
        return;
      }
      event.preventDefault();
      event.returnValue = '';
    }, [])
  );
}

export default useUnsavedChangesPrompt;